import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { OrganizationRepository, OrganizationListOptions, OrganizationListResponse } from '../../domain/repositories/OrganizationRepository';
import { Organization } from '../../domain/entities/Organization';
import { AddressBookEntry } from '../../domain/entities/AddressBookEntry';
import { RepositoryError } from '../../domain/errors';
import { ILoggerService } from '../../domain/services/LoggerService';
import { UserContext } from '../../domain/value-objects/UserContext';

export class FirebaseOrganizationRepository implements OrganizationRepository {
  private readonly collectionName = 'organizations';
  private readonly logger: ILoggerService;

  constructor(logger: ILoggerService) {
    this.logger = logger;
  }

  async findById(id: string): Promise<Organization | null> {
    try {
      const organizationDocRef = doc(db, this.collectionName, id);
      const organizationDocSnap = await getDoc(organizationDocRef);

      if (organizationDocSnap.exists()) {
        const data = organizationDocSnap.data();
        return new Organization(
          organizationDocSnap.id,
          data.name,
          data.configs || {},
          data.contact || {},
          this.mapAddressBook(data.addressBook),
          this.convertFirestoreDate(data.createdAt),
          this.convertFirestoreDate(data.updatedAt),
          data.createdBy,
          data.updatedBy
        );
      }

      return null;
    } catch (error) {
      throw RepositoryError.queryFailed();
    }
  }

  async findAllSimple(userContext?: UserContext): Promise<Organization[]> {
    // Only admin can see all organizations
    if (userContext?.role !== 'admin') {
      // Non-admin users can only see their own organization
      if (userContext?.organizationId) {
        const org = await this.findById(userContext.organizationId);
        return org ? [org] : [];
      }
      return [];
    }
    try {
      const organizationsQuery = query(
        collection(db, this.collectionName),
        orderBy('name', 'asc')
      );

      const querySnapshot = await getDocs(organizationsQuery);

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return new Organization(
          doc.id,
          data.name,
          data.configs || {},
          data.contact || {},
          this.mapAddressBook(data.addressBook),
          this.convertFirestoreDate(data.createdAt),
          this.convertFirestoreDate(data.updatedAt),
          data.createdBy,
          data.updatedBy
        );
      });
    } catch (error) {
      throw RepositoryError.queryFailed();
    }
  }

  async findAll(options: OrganizationListOptions = {}, userContext?: UserContext): Promise<OrganizationListResponse> {
    // Only admin can list all organizations
    if (userContext?.role !== 'admin') {
      // Non-admin users can only see their own organization
      if (userContext?.organizationId) {
        const org = await this.findById(userContext.organizationId);
        return {
          organizations: org ? [org] : [],
          totalCount: org ? 1 : 0,
          totalPages: 1,
          currentPage: 1,
          pageSize: 10
        };
      }
      // Return empty result if no organizationId
      return {
        organizations: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 10
      };
    }
    
    // Admin can see all organizations - continue with existing logic
    try {
      const {
        page = 1,
        pageSize = 10,
        sortBy = 'name',
        sortOrder = 'asc'
      } = options;

      const organizationsCollection = collection(db, this.collectionName);
      
      const baseQuery = organizationsCollection;
      const constraints = [];

      constraints.push(orderBy(this.mapSortField(sortBy), sortOrder));
      constraints.push(limit(pageSize));

      let organizationQuery = query(baseQuery, ...constraints);

      // Handle pagination
      if (page > 1) {
        const skipCount = (page - 1) * pageSize;
        const tempConstraints = [];
        
        tempConstraints.push(orderBy(this.mapSortField(sortBy), sortOrder));
        tempConstraints.push(limit(skipCount));
        
        const tempQuery = query(baseQuery, ...tempConstraints);
        const tempSnapshot = await getDocs(tempQuery);
        
        if (tempSnapshot.docs.length > 0) {
          const lastDoc = tempSnapshot.docs[tempSnapshot.docs.length - 1];
          const paginatedConstraints = [];
          
          paginatedConstraints.push(orderBy(this.mapSortField(sortBy), sortOrder));
          paginatedConstraints.push(startAfter(lastDoc));
          paginatedConstraints.push(limit(pageSize));
          
          organizationQuery = query(baseQuery, ...paginatedConstraints);
        }
      }

      // Get paginated results and total count
      const [querySnapshot, totalSnapshot] = await Promise.all([
        getDocs(organizationQuery),
        this.getTotalCount()
      ]);

      const organizations = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return new Organization(
          doc.id,
          data.name,
          data.configs || {},
          data.contact || {},
          this.mapAddressBook(data.addressBook),
          this.convertFirestoreDate(data.createdAt),
          this.convertFirestoreDate(data.updatedAt),
          data.createdBy,
          data.updatedBy
        );
      });

      const totalCount = totalSnapshot;
      const totalPages = Math.ceil(totalCount / pageSize);

      return {
        organizations,
        totalCount,
        totalPages,
        currentPage: page,
        pageSize
      };
    } catch (error) {
      console.error('FirebaseOrganizationRepository.findAll', error);
      throw RepositoryError.queryFailed();
    }
  }

  private async getTotalCount(): Promise<number> {
    try {
      const organizationsCollection = collection(db, this.collectionName);
      const snapshot = await getDocs(organizationsCollection);
      return snapshot.size;
    } catch (error) {
      throw RepositoryError.queryFailed();
    }
  }

  private mapSortField(sortBy: string): string {
    switch (sortBy) {
      default:
        return sortBy;
    }
  }

  private mapAddressBook(raw: any): AddressBookEntry[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter((item: any) => item && typeof item.id === 'string' && typeof item.name === 'string')
      .map((item: any) => AddressBookEntry.fromPersistence(
        item.id,
        item.name,
        typeof item.email === 'string' && item.email ? item.email : undefined,
        typeof item.phone === 'string' && item.phone ? item.phone : undefined
      ));
  }

  private convertFirestoreDate(value: any): Date {
    if (!value) {
      return new Date();
    }
    
    // If it's already a Date object
    if (value instanceof Date) {
      return value;
    }
    
    // If it's a Firestore Timestamp with toDate method
    if (value && typeof value.toDate === 'function') {
      return value.toDate();
    }
    
    // If it's a timestamp number
    if (typeof value === 'number') {
      return new Date(value);
    }
    
    // If it's an object with seconds (Firestore timestamp format)
    if (value && typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
    
    // Fallback to current date
    return new Date();
  }
}